import { create } from 'zustand';
import { produce } from 'immer';

export type TableSortDirection = 'asc' | 'desc';

interface TableState {
  pageCount: {
    [tableId: string]: number;
  };
  pageNumber: {
    [tableId: string]: number;
  };
  pageSize: {
    [tableId: string]: number;
  };
  recordCount: {
    [tableId: string]: number;
  };
  sortColumn: {
    [tableId: string]: string;
  };
  sortDirection: {
    [tableId: string]: TableSortDirection;
  };

  getPageCount: (tableId: string) => number;
  getPageNumber: (tableId: string) => number;
  getPageSize: (tableId: string) => number;
  getRecordCount: (tableId: string) => number;
  getSortColumn: (tableId: string) => string;
  getSortDirection: (tableId: string) => TableSortDirection;

  resetTableState: (tableId: string) => void;
  setPageCount: (tableId: string, pageCount: number) => void;
  setPageNumber: (tableId: string, pageNumber: number) => void;
  setPageSize: (tableId: string, pageSize: number) => void;
  setRecordCount: (tableId: string, recordCount: number) => void;
  setSortColumn: (tableId: string, sortColumn: string) => void;
  setSortDirection: (tableId: string, sortDirection: TableSortDirection) => void;
  toggleSortOrder: (tableId: string) => void;
}

export const useTableStore = create<TableState>((set, get) => ({
  pageCount: {},
  pageNumber: {},
  pageSize: {},
  recordCount: {},
  sortColumn: {},
  sortDirection: {},

  getPageCount: (tableId: string) => get().pageCount[tableId] || 0,
  getPageNumber: (tableId: string) => get().pageNumber[tableId] || 1,
  getPageSize: (tableId: string) => get().pageSize[tableId] || 10,
  getRecordCount: (tableId: string) => get().recordCount[tableId] || 0,
  getSortColumn: (tableId: string) => get().sortColumn[tableId] || '',
  getSortDirection: (tableId: string) => get().sortDirection[tableId] || 'desc',
  resetTableState: (tableId: string) =>
    set((state) => ({
      pageCount: produce(state.pageCount, (draft) => {
        delete draft[tableId];
      }),
      pageNumber: produce(state.pageNumber, (draft) => {
        delete draft[tableId];
      }),
      sortColumn: produce(state.sortColumn, (draft) => {
        delete draft[tableId];
      }),
      sortDirection: produce(state.sortDirection, (draft) => {
        delete draft[tableId];
      }),
      pageSize: produce(state.pageSize, (draft) => {
        delete draft[tableId];
      }),
      recordCount: produce(state.recordCount, (draft) => {
        delete draft[tableId];
      }),
    })),
  setPageCount: (tableId: string, pageCount: number) => {
    set((state) => ({
      pageCount: produce(state.pageCount, (draft) => {
        draft[tableId] = pageCount;
      }),
    }));
  },
  setPageNumber: (tableId: string, pageNumber: number) =>
    set((state) => ({
      pageNumber: produce(state.pageNumber, (draft) => {
        draft[tableId] = pageNumber;
      }),
    })),
  setPageSize: (tableId: string, pageSize: number) =>
    set((state) => ({
      pageSize: produce(state.pageSize, (draft) => {
        draft[tableId] = pageSize;
      }),
    })),
  setRecordCount: (tableId: string, recordCount: number) => {
    const pageSize = get().pageSize[tableId] || 10;
    const pageCount = Math.ceil(recordCount / pageSize);

    get().setPageCount(tableId, pageCount);
    set((state) => ({
      recordCount: produce(state.recordCount, (draft) => {
        draft[tableId] = recordCount;
      }),
    }));
  },
  setSortColumn: (tableId: string, sortColumn: string) =>
    set((state) => ({
      sortColumn: produce(state.sortColumn, (draft) => {
        draft[tableId] = sortColumn;
      }),
    })),
  setSortDirection: (tableId: string, sortDirection: TableSortDirection) =>
    set((state) => ({
      sortDirection: produce(state.sortDirection, (draft) => {
        draft[tableId] = sortDirection;
      }),
    })),
  toggleSortOrder: (tableId: string) =>
    set((state) => ({
      sortDirection: produce(state.sortDirection, (draft) => {
        draft[tableId] = draft[tableId] === 'asc' ? 'desc' : 'asc';
      }),
    })),
}));
